import { useAuthStore } from '@/state/store/auth';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { graphqlClient } from '@/lib/graphql-client';
import { buildPagingLiteral, buildWhereLiteral } from '@/lib/graphql-query-builder';
import { EmployeeSnapshot } from './service-desk.types';

const buildGetServiceDeskEmployeeQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    Employees: getUserProfileDetails(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      items {
        DisplayName
        Email
        PhoneNumber
        EmployeeID
        Designation
        DepartmentId
        Sex
        JoiningDate
      }
    }
  }
`;

/** Blood group and passport number are personal details, kept on the person's `UserContactDetail`. */
const buildGetServiceDeskContactQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    ContactDetails: getUserContactDetails(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      items {
        BloodGroup
        PassportNo
      }
    }
  }
`;

const buildGetServiceDeskHrProfileQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    UserHRPeriodicalProfiles: getUserHRPeriodicalProfiles(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      items {
        EmploymentType
      }
    }
  }
`;

interface EmployeeRow {
  DisplayName?: string;
  Email?: string;
  PhoneNumber?: string;
  EmployeeID?: string;
  Designation?: string;
  DepartmentId?: string;
  Sex?: string;
  JoiningDate?: string;
}

interface ContactRow {
  BloodGroup?: string;
  PassportNo?: string;
}

const fetchContactDetail = async (userId: string): Promise<ContactRow> => {
  const result = await graphqlClient.query<{ ContactDetails: { items: ContactRow[] } }>({
    query: buildGetServiceDeskContactQuery({ UserId: userId }, 1, 1),
  });
  return result.ContactDetails.items[0] ?? {};
};

export const useCurrentUserId = () => useAuthStore((state) => state.user?.itemId ?? '');

/**
 * The employment type of the applicant's *current HR year* (legacy `UserHRPeriodicalProfile`). Terms
 * carry the IAM user id in `UserId` (hr-directory and the hr-term migration both write it).
 */
const fetchCurrentEmploymentType = async (userId: string): Promise<string> => {
  const result = await graphqlClient.query<{
    UserHRPeriodicalProfiles: { items: { EmploymentType?: string }[] };
  }>({
    query: buildGetServiceDeskHrProfileQuery({ UserId: userId, IsCurrentHrYear: true }, 1, 1),
  });
  return result.UserHRPeriodicalProfiles.items[0]?.EmploymentType ?? '';
};

/**
 * `userId` is the signed-in IAM user's own id. Every service-desk application (Business
 * Card/MetLife/BYOD/NOC/ID Card) is stamped with, and its "my applications" list is filtered by,
 * this same id — see `buildNewApplicationStamp` below and e.g. `metlife-my-applications.tsx`.
 */
export const fetchEmployeeSnapshot = async (userId: string): Promise<EmployeeSnapshot | null> => {
  const employeeResult = await graphqlClient.query<{ Employees: { items: EmployeeRow[] } }>({
    query: buildGetServiceDeskEmployeeQuery({ UserId: userId }, 1, 1),
  });

  const row = employeeResult.Employees.items[0];
  if (!row) return null;

  const [hrEmploymentType, contact] = await Promise.all([
    fetchCurrentEmploymentType(userId),
    fetchContactDetail(userId),
  ]);

  return {
    userId,
    displayName: row.DisplayName ?? '',
    email: row.Email ?? '',
    phoneNumber: row.PhoneNumber ?? '',
    employeeId: row.EmployeeID ?? '',
    designation: row.Designation ?? '',
    departmentId: row.DepartmentId ?? '',
    sex: row.Sex ?? '',
    bloodGroup: contact.BloodGroup ?? '',
    passportNo: contact.PassportNo ?? '',
    joiningDate: row.JoiningDate ?? '',
    employmentType: hrEmploymentType,
  };
};

/** The signed-in employee's profile facts; `snapshot` is null until loaded (or if no record exists). */
export const useEmployeeSnapshot = () => {
  const userId = useCurrentUserId();
  const query = useGlobalQuery({
    queryKey: ['hr-service-desk-employee', userId],
    queryFn: () => fetchEmployeeSnapshot(userId),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
  return { snapshot: query.data ?? null, isLoading: query.isLoading };
};
